import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import { PageHeader } from '@opengov/components-page-header';
import { AccountPlus, Magnify } from '@opengov/react-capital-assets';

import { CaseStatusBadge } from '../components/vcm/CaseStatusBadge';
import { SectionCard } from '../components/vcm/SectionCard';
import { TierBadge } from '../components/vcm/TierBadge';
import { useVcm } from '../contexts/VcmContext';
import { useAppNavigation } from '../hooks/useAppNavigation';
import { TEXAS_COUNTIES } from '../lib/vcm/counties';
/* MOCK DATA — replace with real /v2 API calls when backend is connected */
import {
  mockGetClients,
  mockGetCases,
  mockGetAddressRecords,
  mockGetHouseholdByClientId,
  mockGetAllFollowUps,
} from '../mocks/vcm-mock-data';
import type { AddressRecord, TcrCase, TcrClient, TcrHouseholdMember } from '../types/vcm';

type SearchMode = 'client' | 'case' | 'address';

/* MOCK DATA — filter options */
const EVENT_OPTIONS = ['', 'SSVF – Austin Area', 'Veterans Housing Assistance – South TX'] as const;
const COUNTY_OPTIONS = ['All counties', ...TEXAS_COUNTIES] as const;
const DISASTER_OPTIONS = ['All programs', 'SSVF-TX-2026-001', 'VHAP-TX-2026-001'] as const;
const ORGANIZATION_OPTIONS = [
  'All organizations',
  'VSO Region 6',
  'United Way',
  'Volunteers of America',
] as const;
const STATUS_OPTIONS = [
  'All statuses',
  'Open',
  'Pending',
  'Review',
  'On Wait List',
  'Closed',
] as const;

/** Dashboard deep-link focuses that force case mode + a derived case filter. */
const FOCUS_VALUES = ['expiring-housing', 'overdue-followups', 'pending-referrals'] as const;
type FocusValue = (typeof FOCUS_VALUES)[number];
const FOCUS_LABELS: Record<FocusValue, string> = {
  'expiring-housing': 'Temporary housing expiring within 30 days',
  'overdue-followups': 'Cases with overdue follow-ups',
  'pending-referrals': 'Cases pending review / referrals',
};

/** Format a YYYY-MM-DD date of birth as "Jan 1, 1980". */
function formatDob(value: string): string {
  return parseDateOnly(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function normalizeMode(value: string | null): SearchMode {
  return value === 'case' || value === 'address' ? value : 'client';
}

function normalizeStatus(value: string | null): string {
  if (!value) return 'All statuses';
  const match = STATUS_OPTIONS.find((opt) => opt.toLowerCase() === value.toLowerCase());
  return match ?? 'All statuses';
}

function normalizeFocus(value: string | null): FocusValue | '' {
  return FOCUS_VALUES.includes(value as FocusValue) ? (value as FocusValue) : '';
}

/** Midnight today, for date-only comparisons. */
function startOfToday(): Date {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return now;
}

/** Parse a YYYY-MM-DD string as a local date (avoids UTC offset surprises). */
function parseDateOnly(value: string): Date {
  return new Date(`${value}T00:00:00`);
}

/** Whole days from today until the given date (negative = in the past). */
function daysUntil(value: string): number {
  return Math.round((parseDateOnly(value).getTime() - startOfToday().getTime()) / 86_400_000);
}

export function ClientSearchPage() {
  const { navigateToPath } = useAppNavigation();
  const { currentEvent } = useVcm();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialFocus = normalizeFocus(searchParams.get('focus'));

  const [searchMode, setSearchMode] = useState<SearchMode>(() =>
    initialFocus ? 'case' : normalizeMode(searchParams.get('type')),
  );
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('q') ?? '');
  const [eventFilter, setEventFilter] = useState(
    () => searchParams.get('event') ?? currentEvent.name,
  );
  const [countyFilter, setCountyFilter] = useState(
    () => searchParams.get('county') ?? 'All counties',
  );
  const [programFilter, setProgramFilter] = useState(
    () => searchParams.get('disaster') ?? 'All programs',
  );
  const [organizationFilter, setOrganizationFilter] = useState(
    () => searchParams.get('org') ?? 'All organizations',
  );
  const [statusFilter, setStatusFilter] = useState(() =>
    normalizeStatus(searchParams.get('status')),
  );
  const [focus, setFocus] = useState<FocusValue | ''>(() => initialFocus);

  /** Keeps URL query params in sync so dashboard deep-links round-trip. */
  const syncParam = useCallback(
    (key: string, value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set(key, value);
          else next.delete(key);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  /** Reset the event filter whenever the active event changes (not on mount). */
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    setEventFilter(currentEvent.name);
    syncParam('event', currentEvent.name);
  }, [currentEvent.name, syncParam]);

  /* MOCK DATA */
  const clients = useMemo(() => mockGetClients(), []);
  const cases = useMemo(() => mockGetCases(), []);
  const addressRecords = useMemo(() => mockGetAddressRecords(), []);

  /** Household members per listed client, so search matches member names too. */
  const householdByClient = useMemo(() => {
    const map = new Map<string, TcrHouseholdMember[]>();
    for (const client of clients) {
      map.set(client.id, mockGetHouseholdByClientId(client.id));
    }
    return map;
  }, [clients]);

  /** Case IDs whose follow-up is past due (used by the overdue-followups focus). */
  const overdueCaseIds = useMemo(() => {
    const today = startOfToday();
    const ids = new Set<string>();
    for (const followUp of mockGetAllFollowUps()) {
      if (followUp.nextFollowUpDate && parseDateOnly(followUp.nextFollowUpDate) < today) {
        ids.add(followUp.caseId);
      }
    }
    return ids;
  }, []);

  const filteredClients = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    const matches: { client: TcrClient; householdMatch: string | null }[] = [];
    for (const c of clients) {
      if (eventFilter && c.program !== eventFilter) continue;
      if (countyFilter !== 'All counties' && c.county !== countyFilter) continue;
      if (programFilter !== 'All programs' && c.programCode !== programFilter) continue;
      if (organizationFilter !== 'All organizations' && c.organization !== organizationFilter)
        continue;
      if (statusFilter !== 'All statuses' && c.status !== statusFilter) continue;

      if (!q) {
        matches.push({ client: c, householdMatch: null });
        continue;
      }

      const directMatch = `${c.firstName} ${c.lastName} ${c.id} ${c.phoneHome ?? ''} ${c.email ?? ''}`
        .toLowerCase()
        .includes(q);

      let householdMatch: string | null = null;
      if (!directMatch) {
        const member = (householdByClient.get(c.id) ?? []).find(
          (m) =>
            m.relationship !== 'Self' &&
            `${m.firstName} ${m.lastName}`.toLowerCase().includes(q),
        );
        if (member) householdMatch = `${member.firstName} ${member.lastName}`;
      }

      if (directMatch || householdMatch) matches.push({ client: c, householdMatch });
    }
    return matches;
  }, [
    clients,
    householdByClient,
    searchQuery,
    eventFilter,
    countyFilter,
    programFilter,
    organizationFilter,
    statusFilter,
  ]);

  const filteredCases = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return cases.filter((c) => {
      if (
        q &&
        !`${c.caseId} ${c.clientName} ${c.tier} ${c.assignedManager} ${c.progression}`
          .toLowerCase()
          .includes(q)
      )
        return false;
      if (eventFilter && c.program !== eventFilter) return false;
      if (countyFilter !== 'All counties' && c.county !== countyFilter) return false;
      if (programFilter !== 'All programs' && c.programCode !== programFilter) return false;
      if (organizationFilter !== 'All organizations' && c.organization !== organizationFilter)
        return false;
      if (statusFilter !== 'All statuses' && c.status !== statusFilter) return false;
      return true;
    });
  }, [
    cases,
    searchQuery,
    eventFilter,
    countyFilter,
    programFilter,
    organizationFilter,
    statusFilter,
  ]);

  /** Cases after applying the active dashboard focus on top of the filters. */
  const focusedCases = useMemo(() => {
    if (!focus) return filteredCases;
    if (focus === 'expiring-housing') {
      return filteredCases.filter((c) => {
        if (!c.temporaryHousingEndDate) return false;
        const days = daysUntil(c.temporaryHousingEndDate);
        return days >= 0 && days <= 30;
      });
    }
    if (focus === 'overdue-followups') {
      return filteredCases.filter((c) => overdueCaseIds.has(c.caseId));
    }
    return filteredCases.filter((c) => c.status === 'Review');
  }, [focus, filteredCases, overdueCaseIds]);

  const filteredAddresses = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return addressRecords.filter((a) => {
      if (q && !`${a.address} ${a.clientName} ${a.county}`.toLowerCase().includes(q)) return false;
      if (eventFilter && a.program !== eventFilter) return false;
      if (countyFilter !== 'All counties' && a.county !== countyFilter) return false;
      if (programFilter !== 'All programs' && a.programCode !== programFilter) return false;
      return true;
    });
  }, [addressRecords, searchQuery, eventFilter, countyFilter, programFilter]);

  const clearFocus = () => {
    setFocus('');
    syncParam('focus', '');
  };

  const handleModeChange = (_: React.MouseEvent<HTMLElement>, value: SearchMode | null) => {
    if (value) {
      setSearchMode(value);
      syncParam('type', value === 'client' ? '' : value);
      if (focus) clearFocus();
    }
  };

  const handleClientRowClick = (client: TcrClient) => navigateToPath(`clients/${client.id}`);
  const handleCaseRowClick = (tcrCase: TcrCase) => navigateToPath(`cases/${tcrCase.caseId}`);
  const handleAddressRowClick = (record: AddressRecord) =>
    navigateToPath(`clients/${record.clientId}`);

  const resultCount =
    searchMode === 'client'
      ? filteredClients.length
      : searchMode === 'case'
        ? focusedCases.length
        : filteredAddresses.length;

  const resultLabel =
    searchMode === 'client'
      ? `${resultCount} client${resultCount !== 1 ? 's' : ''}`
      : searchMode === 'case'
        ? `${resultCount} case${resultCount !== 1 ? 's' : ''}`
        : `${resultCount} address${resultCount !== 1 ? 'es' : ''}`;

  return (
    <Box
      sx={{
        p: (theme) => theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        gap: (theme) => theme.spacing(2.5),
      }}
    >
      <PageHeader
        title="Client Search"
        actions={[
          <Button
            key="new-client"
            variant="contained"
            startIcon={<AccountPlus />}
            onClick={() => navigateToPath('clients/new')}
          >
            New Client
          </Button>,
        ]}
      />

      {focus && (
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={clearFocus}>
              Clear filter
            </Button>
          }
        >
          Showing a focused view: {FOCUS_LABELS[focus]}.
        </Alert>
      )}

      <SectionCard title="Search">
        <Stack spacing={2}>
          <ToggleButtonGroup
            value={searchMode}
            exclusive
            onChange={handleModeChange}
            size="small"
            aria-label="Search type"
          >
            <ToggleButton value="client">Client</ToggleButton>
            <ToggleButton value="case">Case</ToggleButton>
            <ToggleButton value="address">Address</ToggleButton>
          </ToggleButtonGroup>

          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }} useFlexGap>
            <TextField
              select
              label="Event"
              value={eventFilter}
              onChange={(e) => {
                setEventFilter(e.target.value);
                syncParam('event', e.target.value);
              }}
              size="small"
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All events</MenuItem>
              {EVENT_OPTIONS.filter(Boolean).map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="County"
              value={countyFilter}
              onChange={(e) => {
                setCountyFilter(e.target.value);
                syncParam('county', e.target.value === 'All counties' ? '' : e.target.value);
              }}
              size="small"
              sx={{ minWidth: 160 }}
            >
              {COUNTY_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Program"
              value={programFilter}
              onChange={(e) => {
                setProgramFilter(e.target.value);
                syncParam('disaster', e.target.value === 'All programs' ? '' : e.target.value);
              }}
              size="small"
              sx={{ minWidth: 160 }}
            >
              {DISASTER_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Organization"
              value={organizationFilter}
              onChange={(e) => {
                setOrganizationFilter(e.target.value);
                syncParam('org', e.target.value === 'All organizations' ? '' : e.target.value);
              }}
              size="small"
              sx={{ minWidth: 200 }}
            >
              {ORGANIZATION_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                syncParam('status', e.target.value === 'All statuses' ? '' : e.target.value);
              }}
              size="small"
              sx={{ minWidth: 140 }}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </TextField>
          </Stack>

          <TextField
            placeholder={
              searchMode === 'client'
                ? 'Search by name, ID, phone, email, or household member…'
                : searchMode === 'case'
                  ? 'Search by case ID, client, tier, manager, or progression…'
                  : 'Search by address, client name, or county…'
            }
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              syncParam('q', e.target.value);
            }}
            size="small"
            fullWidth
            slotProps={{
              input: {
                startAdornment: (
                  <Box
                    sx={{
                      display: 'flex',
                      mr: 1,
                      color: (t) => t.palette.text.secondary,
                      '& svg': { width: 18, height: 18 },
                    }}
                  >
                    <Magnify />
                  </Box>
                ),
              },
            }}
          />
        </Stack>
      </SectionCard>

      <SectionCard title={`Results (${resultLabel})`} disableContentPadding>
        <TableContainer sx={{ maxHeight: 480 }}>
          {searchMode === 'client' && (
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '25%' }}>Name</TableCell>
                  <TableCell align="center" sx={{ width: '15%' }}>County</TableCell>
                  <TableCell align="center" sx={{ width: '25%' }}>Organization</TableCell>
                  <TableCell align="center" sx={{ width: 90 }}>Open Cases</TableCell>
                  <TableCell align="center" sx={{ width: 80 }}>Tier</TableCell>
                  <TableCell align="center" sx={{ width: 90 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredClients.map(({ client, householdMatch }) => (
                    <TableRow
                      key={client.id}
                      hover
                      onClick={() => handleClientRowClick(client)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                          <Avatar
                            sx={{
                              width: 28,
                              height: 28,
                              mt: 0.25,
                              fontSize: (theme) => theme.typography.caption.fontSize,
                              bgcolor: (theme) => theme.palette.primary.main,
                              flexShrink: 0,
                            }}
                          >
                            {getInitials(`${client.firstName} ${client.lastName}`)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {client.firstName} {client.lastName}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: (theme) => theme.palette.text.secondary, display: 'block' }}
                            >
                              {client.id}
                              {client.dateOfBirth ? ` · DOB: ${formatDob(client.dateOfBirth)}` : ''}
                              {client.phoneHome ? ` · ${client.phoneHome}` : ''}
                            </Typography>
                            {householdMatch && (
                              <Typography
                                variant="caption"
                                sx={{ color: (theme) => theme.palette.text.secondary, display: 'block' }}
                              >
                                Household match: {householdMatch}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell align="center">{client.county}</TableCell>
                      <TableCell align="center">{client.organization}</TableCell>
                      <TableCell align="center">
                        <Chip label={client.openCases} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="center">
                        <TierBadge tier={client.tier} />
                      </TableCell>
                      <TableCell align="center">
                        <CaseStatusBadge status={client.status} />
                      </TableCell>
                    </TableRow>
                ))}
                {filteredClients.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: (theme) => theme.spacing(4) }}>
                      <Typography
                        variant="body2"
                        sx={{ color: (theme) => theme.palette.text.secondary }}
                      >
                        No clients match the current filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}

          {searchMode === 'case' && (
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align="center">Case ID</TableCell>
                  <TableCell align="center">Client</TableCell>
                  <TableCell align="center">Program / Code</TableCell>
                  <TableCell align="center">Organization</TableCell>
                  <TableCell align="center" sx={{ width: 100 }}>Progression</TableCell>
                  <TableCell align="center" sx={{ width: 80 }}>Tier</TableCell>
                  <TableCell align="center" sx={{ width: 90 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {focusedCases.map((tcrCase) => (
                  <TableRow
                    key={tcrCase.caseId}
                    hover
                    onClick={() => handleCaseRowClick(tcrCase)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {tcrCase.caseId}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{tcrCase.clientName}</TableCell>
                    <TableCell align="center">
                      <Stack>
                        <Typography variant="body2">{tcrCase.program}</Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: (theme) => theme.palette.text.secondary }}
                        >
                          {tcrCase.programCode}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="center">{tcrCase.organization}</TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">{tcrCase.progression}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <TierBadge tier={tcrCase.tier} />
                    </TableCell>
                    <TableCell align="center">
                      <CaseStatusBadge status={tcrCase.status} />
                    </TableCell>
                  </TableRow>
                ))}
                {focusedCases.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: (theme) => theme.spacing(4) }}>
                      <Typography
                        variant="body2"
                        sx={{ color: (theme) => theme.palette.text.secondary }}
                      >
                        No cases match the current filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}

          {searchMode === 'address' && (
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align="center">Address</TableCell>
                  <TableCell align="center">Client</TableCell>
                  <TableCell align="center">Type</TableCell>
                  <TableCell align="center">County</TableCell>
                  <TableCell align="center">Event</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAddresses.map((record) => (
                  <TableRow
                    key={`${record.clientId}-${record.addressType}`}
                    hover
                    onClick={() => handleAddressRowClick(record)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {record.address}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{record.clientName}</TableCell>
                    <TableCell align="center">
                      <Chip label={record.addressType} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="center">{record.county}</TableCell>
                    <TableCell align="center">
                      <Stack>
                        <Typography variant="body2">{record.program}</Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: (theme) => theme.palette.text.secondary }}
                        >
                          {record.programCode}
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredAddresses.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: (theme) => theme.spacing(4) }}>
                      <Typography
                        variant="body2"
                        sx={{ color: (theme) => theme.palette.text.secondary }}
                      >
                        No addresses match the current filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </SectionCard>
    </Box>
  );
}
